import { useState, useRef, useEffect } from "react";
import { Search, MapPin, Loader2, X } from "lucide-react";
import { geocode } from "../lib/api";

export const SearchBar = ({ onSelect }) => {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const boxRef = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const handleChange = (val) => {
    setQ(val);
    clearTimeout(timer.current);
    if (val.trim().length < 2) {
      setResults([]);
      setOpen(false);
      return;
    }
    setLoading(true);
    setOpen(true);
    timer.current = setTimeout(async () => {
      try {
        const res = await geocode(val.trim());
        setResults(res);
      } catch {
        setResults([]);
      } finally {
        setLoading(false);
      }
    }, 350);
  };

  const pick = (r) => {
    onSelect(r);
    setQ("");
    setResults([]);
    setOpen(false);
  };

  return (
    <div className="relative w-full" ref={boxRef}>
      <div className="flex items-center gap-2 glass rounded-2xl px-4 h-12">
        <Search className="w-4 h-4 text-muted-foreground shrink-0" />
        <input
          data-testid="city-search-input"
          value={q}
          onChange={(e) => handleChange(e.target.value)}
          onFocus={() => q.length >= 2 && setOpen(true)}
          placeholder="Cari kota, kecamatan, desa..."
          className="bg-transparent outline-none w-full text-sm placeholder:text-muted-foreground"
        />
        {loading && <Loader2 className="w-4 h-4 animate-spin text-muted-foreground" />}
        {q && !loading && (
          <button data-testid="search-clear-button" onClick={() => handleChange("")}>
            <X className="w-4 h-4 text-muted-foreground hover:text-foreground" />
          </button>
        )}
      </div>

      {open && results.length > 0 && (
        <div
          data-testid="search-results-dropdown"
          className="absolute z-50 mt-2 w-full glass rounded-2xl overflow-hidden shadow-2xl fade-up"
        >
          {results.map((r, i) => (
            <button
              key={`${r.lat}-${r.lon}-${i}`}
              data-testid={`search-result-${i}`}
              onClick={() => pick(r)}
              className="flex items-center gap-3 w-full px-4 py-3 text-left hover:bg-[hsl(var(--surface-hover))] transition-colors"
            >
              <MapPin className="w-4 h-4 text-primary shrink-0" />
              <span className="text-sm min-w-0">
                <span className="font-semibold">{r.name}</span>
                {r.district || r.regency ? (
                  <span className="block text-xs text-muted-foreground truncate" data-testid={`search-result-detail-${i}`}>
                    {[r.district, r.regency, r.state].filter((p, idx, arr) => p && p !== r.name && arr.indexOf(p) === idx).join(", ")}
                  </span>
                ) : (
                  <>
                    {r.state ? <span className="text-muted-foreground">, {r.state}</span> : null}
                    <span className="text-muted-foreground"> · {r.country}</span>
                  </>
                )}
              </span>
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
